import type { ChangeEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import {
    FileInput,
    type FileInputProps,
} from '@/React/Shared/Component/Forms/FileInput'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'

export type ImagePreviewSize = 'sm' | 'md' | 'lg' | 'xl' | string
export type ImagePreviewPosition = 'bottom' | 'top' | 'side'

export interface ImageFileInputProps extends Omit<FileInputProps, 'ref'> {
    previewUrl?: string | null
    onPreviewUrlChange?: (url: string | null) => void
    onRemove?: () => void
    canRemove?: boolean
    previewSize?: ImagePreviewSize
    previewPosition?: ImagePreviewPosition
    previewImageClasses?: string
    previewContainerClasses?: string
    previewAlt?: string
}

export function resolvePreviewSizeClasses(size?: ImagePreviewSize): string {
    if (!size) {
        return 'h-24 w-24'
    }

    switch (size) {
        case 'sm':
            return 'h-16 w-16'
        case 'md':
            return 'h-24 w-24'
        case 'lg':
            return 'h-36 w-36'
        case 'xl':
            return 'h-48 w-48'
        default:
            return size
    }
}

export function ImageFileInput({
    previewUrl,
    onPreviewUrlChange,
    onRemove,
    canRemove = true,
    previewSize = 'md',
    previewPosition = 'bottom',
    previewImageClasses = '',
    previewContainerClasses = '',
    previewAlt = 'Image preview',
    accept = 'image/*',
    className = '',
    disabled = false,
    onChange,
    ...fileInputProps
}: ImageFileInputProps) {
    const [internalPreviewUrl, setInternalPreviewUrl] = useState<string | null>(
        previewUrl ?? null,
    )
    const createdObjectUrlRef = useRef<string | null>(null)
    const innerInputRef = useRef<HTMLInputElement | null>(null)

    const revokeCreatedObjectUrl = () => {
        if (createdObjectUrlRef.current) {
            if (
                typeof URL !== 'undefined' &&
                typeof URL.revokeObjectURL === 'function'
            ) {
                URL.revokeObjectURL(createdObjectUrlRef.current)
            }

            createdObjectUrlRef.current = null
        }
    }

    const notifyPreviewUrlChange = (url: string | null) => {
        onPreviewUrlChange?.(url)
    }

    useEffect(() => {
        return () => {
            revokeCreatedObjectUrl()
        }
    }, [])

    useEffect(() => {
        if (previewUrl !== undefined) {
            setInternalPreviewUrl(previewUrl)
        }
    }, [previewUrl])

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])

        revokeCreatedObjectUrl()

        if (files.length === 0) {
            setInternalPreviewUrl(null)
            notifyPreviewUrlChange(null)
            onChange?.(event)

            return
        }

        const file = files[0]
        const isImage = file.type ? file.type.startsWith('image/') : true

        if (isImage) {
            if (
                typeof URL !== 'undefined' &&
                typeof URL.createObjectURL === 'function'
            ) {
                const objectUrl = URL.createObjectURL(file)
                createdObjectUrlRef.current = objectUrl
                setInternalPreviewUrl(objectUrl)
                notifyPreviewUrlChange(objectUrl)
            }
        } else {
            setInternalPreviewUrl(null)
            notifyPreviewUrlChange(null)
        }

        onChange?.(event)
    }

    const handleRemove = () => {
        revokeCreatedObjectUrl()
        setInternalPreviewUrl(null)

        if (innerInputRef.current) {
            innerInputRef.current.value = ''
        }

        notifyPreviewUrlChange(null)
        onRemove?.()

        if (onChange && innerInputRef.current) {
            const syntheticEvent = {
                target: innerInputRef.current,
                currentTarget: innerInputRef.current,
            } as ChangeEvent<HTMLInputElement>

            onChange(syntheticEvent)
        }
    }

    const isRemoveAllowed =
        canRemove && !disabled && Boolean(internalPreviewUrl)
    const sizeClasses = resolvePreviewSizeClasses(previewSize)

    const previewBox = internalPreviewUrl ? (
        <div
            className={`relative inline-block ${previewContainerClasses}`.trim()}
        >
            <div
                className={`relative overflow-hidden rounded-lg2 border border-stone-200 bg-stone-50 shadow-xs ${sizeClasses}`.trim()}
            >
                <img
                    src={internalPreviewUrl}
                    alt={previewAlt}
                    className={`h-full w-full object-cover ${previewImageClasses}`.trim()}
                />
            </div>
            {isRemoveAllowed && (
                <button
                    type="button"
                    onClick={handleRemove}
                    aria-label="Remove image"
                    title="Remove image"
                    className="absolute -top-2 -right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white text-stone-500 hover:text-red-600 border border-stone-200 shadow-xs transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                >
                    <Icon
                        name={IconNameEnum.Close}
                        className="w-3.5 h-3.5"
                        aria-hidden="true"
                    />
                </button>
            )}
        </div>
    ) : null

    const fileInput = (
        <FileInput
            {...fileInputProps}
            ref={innerInputRef}
            accept={accept}
            disabled={disabled}
            onChange={handleFileChange}
        />
    )

    if (previewPosition === 'side') {
        return (
            <div className={`flex items-start gap-4 ${className}`.trim()}>
                {previewBox}
                <div className="flex-1 min-w-0">{fileInput}</div>
            </div>
        )
    }

    if (previewPosition === 'top') {
        return (
            <div className={`space-y-3 ${className}`.trim()}>
                {previewBox}
                {fileInput}
            </div>
        )
    }

    return (
        <div className={`space-y-3 ${className}`.trim()}>
            {fileInput}
            {previewBox}
        </div>
    )
}
